import React from "react";

import { useTheme } from "@emotion/react";
import FormatCrypto from "@hl/base-components/lib/FormatCrypto";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { ResizedImage } from "@hl/shared-features/lib/features/image";
import BidModalHighlight from "@hl/shared-features/lib/features/marketplace/modals/BidModal";
import { HighlightFields } from "@hl/shared-features/lib/features/marketplace/utils";
import { useModalStack } from "@hl/shared-features/lib/features/modal";
import { getCurrencySymbol } from "@hl/shared-features/lib/utils/currency";
import {
  Box,
  Button,
  Card,
  Center,
  Flex,
  Grid,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useCollections, useTokens } from "@reservoir0x/reservoir-kit-ui";
import { useParams } from "react-router-dom";

import { MINT_URL } from "~config";
import { InternalLink } from "~features/MintPage/components/InternalLink";
import useMintState from "~hooks/useMintState";

import { shloms404OnImageError } from "../custom/Shloms404Carousel";

type TokenHighlight = ReturnType<typeof useTokens>["data"][0] & HighlightFields;

const MarketplacePreviewGallery = ({
  collectionId,
}: {
  collectionId?: string;
}) => {
  const { pushModal } = useModalStack();
  const { collectionId: paramsCollectionId } = useParams();
  collectionId = collectionId ?? paramsCollectionId;
  const { collection, chainId, reservoirCollectionId } = useMintState();
  const theme = useTheme();

  const marketplaceLink = MINT_URL.tokens.marketplace.replace(
    ":collectionId",
    collectionId ?? ""
  );

  const { data: tokens } = useTokens({
    collection: reservoirCollectionId,
    limit: 16,
  });
  const { data: collectionsData } = useCollections({
    id: reservoirCollectionId,
  });
  const collectionData = collectionsData?.[0];

  if (!tokens?.length) return null;

  return (
    <>
      <Paper mt={40} w={{ md: 380 }} mx="auto" shadow={theme.shadows.sm}>
        <Card withBorder={false}>
          <Stack spacing="md">
            <Stack spacing={0}>
              <Title size="large" lh={1.4} weight={WEIGHT_BOLD}>
                Highlight Marketplace
              </Title>
              <Text size="xs" color={TEXT_COLOR.SECONDARY}>
                {collectionData?.onSaleCount || "N/A"} item
                {collectionData?.onSaleCount === "1" ? "" : "s"} for sale
              </Text>
            </Stack>
            <Grid gutter={8}>
              {(tokens as TokenHighlight[]).map((token) => (
                <Grid.Col
                  key={token.token?.tokenId}
                  pos="relative"
                  span={3}
                  xs={2}
                  md={3}
                >
                  <Box
                    component={InternalLink}
                    pos="relative"
                    display="flex"
                    sx={{ aspectRatio: "1" }}
                    to={MINT_URL.tokens.detail
                      .replace(":collectionId", collection?.id ?? "")
                      .replace(":tokenId", token.token?.tokenId ?? "")}
                  >
                    <ResizedImage
                      src={
                        token.token?.imageSmall ||
                        token.token?.image ||
                        token.highlightToken?.imageUrl ||
                        collection?.collectionImage
                      }
                      fitSize={152}
                      aspectRatio="1"
                      fit="cover"
                      sx={{
                        textDecoration: "none",
                        border: `1px solid ${theme.colors.divider[0]}`,
                        background: theme.colors.divider[0],
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onError={
                        collection?.flagVariations.shloms404UI
                          ? shloms404OnImageError
                          : undefined
                      }
                    />
                    <Flex
                      pos="absolute"
                      justify="center"
                      align="flex-end"
                      w="100%"
                      bottom="0"
                      pb={8}
                      h="100%"
                      sx={{
                        opacity: 0,
                        backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 50%, rgba(0,0,0,.5) 60%, rgba(0,0,0,.8) 100%)`,
                        transition: "opacity 200ms linear",
                        "&:hover": {
                          opacity: token.market?.floorAsk?.price ? 1 : 0,
                        },
                      }}
                    >
                      {token.market?.floorAsk?.price?.amount?.native && (
                        <Flex
                          gap={4}
                          align="baseline"
                          wrap="wrap"
                          justify="center"
                        >
                          <FormatCrypto
                            size="xs"
                            sx={{ color: "white" }}
                            lh={1}
                            amount={
                              token.market?.floorAsk?.price?.amount?.native
                            }
                          />
                          <Text
                            size={9}
                            tt="uppercase"
                            sx={{ color: "white" }}
                            lh={1}
                          >
                            {getCurrencySymbol(chainId)}
                          </Text>
                        </Flex>
                      )}
                    </Flex>
                    {!token.highlightToken?.imageUrl &&
                      !token.token?.imageSmall &&
                      !token.token?.image &&
                      collection?.collectionImage && (
                        <Center pos="absolute" w="100%" h="100%">
                          <Flex
                            bg={theme.colors.secondaryText[0]}
                            sx={{
                              borderRadius: "100%",
                              transitionDuration: "0.8s",
                              transitionProperty: "transform",
                              transform: "rotate(360deg)",
                            }}
                          >
                            <Loader color={theme.colors.primaryBackground[0]} />
                          </Flex>
                        </Center>
                      )}
                  </Box>
                </Grid.Col>
              ))}
            </Grid>
            <Stack spacing={8}>
              <Button
                component={InternalLink}
                to={marketplaceLink}
                size="lg"
                px="sm"
              >
                View collection on marketplace
              </Button>
              {collection && (
                <Button
                  size="lg"
                  color={SECONDARY_COLOR}
                  px="sm"
                  onClick={() =>
                    pushModal(
                      <BidModalHighlight
                        src={collectionData?.image ?? ""}
                        collectionData={collection}
                      />,
                      {
                        size: "lg",
                        title: (
                          <Text size="md" fw={WEIGHT_BOLD}>
                            Make an offer
                          </Text>
                        ),
                      }
                    )
                  }
                >
                  Make collection offer
                </Button>
              )}
            </Stack>
          </Stack>
        </Card>
      </Paper>
    </>
  );
};
export default MarketplacePreviewGallery;
